import { useEffect, useState } from 'react'
import {
  Banknote,
  HelpCircle,
  MessageSquare,
  ShoppingCart,
  Sparkles,
  Tag,
  Truck,
  X,
} from 'lucide-react'
import { IconButton } from '../ui/IconButton.jsx'
import { cn } from '../../lib/cn.js'
import { isOrderStatusMenuVisible } from '../../lib/chatUtils.js'
import { openMenuUrl, performMenuItemAction } from '../../lib/menuItemActions.js'

const MENU_ANIM_MS = 300

function trimColor(v) {
  if (v == null) return ''
  const s = typeof v === 'string' ? v.trim() : String(v).trim()
  return s
}

function itemIcon(actionType) {
  if (actionType === 'url') return Tag
  if (actionType === 'leadForm') return HelpCircle
  return Sparkles
}

/**
 * In-panel left sheet menu (styled hamburger drawer).
 * Uses the same data-state CSS animations as slide-out / overlay layouts.
 */
export function ChatMenuDrawer({
  open,
  onClose,
  conf,
  menuItems = [],
  onRestartConversation,
  onOpenOrderStatus,
  onSendMenuAction,
  ochatbot,
  openForm,
  setActiveLeadForms,
}) {
  const [mounted, setMounted] = useState(open)
  const [state, setState] = useState(open ? 'open' : 'closed')

  useEffect(() => {
    if (open) {
      setMounted(true)
      let raf2 = 0
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setState('open'))
      })
      return () => {
        cancelAnimationFrame(raf1)
        if (raf2) cancelAnimationFrame(raf2)
      }
    }
    setState('closed')
    const timer = window.setTimeout(() => setMounted(false), MENU_ANIM_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  if (!mounted) return null

  const btnBg = trimColor(conf?.widgetButtonColor) || 'var(--color-brand)'
  const btnFg = trimColor(conf?.widgetButtonTextColor) || '#ffffff'
  const showOrderStatus = isOrderStatusMenuVisible(conf)
  const ecommerce = conf?.menuEcommerce || {}
  const showCart =
    ecommerce.available && ecommerce.showViewCart !== false && ecommerce.viewCartUrl
  const showCheckout =
    ecommerce.available &&
    ecommerce.showCheckout !== false &&
    ecommerce.checkoutUrl

  const handleItemClick = async (item) => {
    await performMenuItemAction({
      item,
      ochatbot,
      conf,
      onSendMenuAction,
      openForm,
      setActiveLeadForms,
    })
    onClose()
  }

  const backdropAnim = cn(
    'animate-in animate-out fade-in fade-out',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=open]:fade-in data-[state=closed]:fade-out',
  )

  const drawerAnim = cn(
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=open]:duration-300 data-[state=closed]:duration-300',
    'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
    'ease-[cubic-bezier(0.4,0,0.2,1)]',
  )

  return (
    <>
      <button
        type="button"
        data-state={state}
        className={cn(
          'absolute inset-0 z-20 bg-black/25',
          backdropAnim,
          state === 'closed' && 'pointer-events-none',
        )}
        aria-label="Close menu"
        aria-hidden={state === 'closed'}
        tabIndex={state === 'open' ? 0 : -1}
        onClick={onClose}
      />
      <aside
        data-state={state}
        className={cn(
          'absolute left-0 top-0 z-30 flex h-full w-[min(280px,85%)] flex-col',
          'border-r border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg',
          'rounded-tr-lg rounded-br-lg',
          drawerAnim,
        )}
        role="dialog"
        aria-label="Chat menu"
        aria-modal="true"
      >
        <div className="flex items-center justify-end px-2 pt-2">
          <IconButton aria-label="Close menu" onClick={onClose} className="h-8 w-8">
            <X className="h-4 w-4" />
          </IconButton>
        </div>

        <div className="flex flex-col gap-3 px-4 pb-4">
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90"
            style={{ backgroundColor: btnBg, color: btnFg }}
            onClick={() => {
              onRestartConversation?.()
              onClose()
            }}
          >
            <MessageSquare className="h-4 w-4 shrink-0" aria-hidden />
            New Conversation
          </button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-2">
          <ul className="flex flex-col gap-0.5">
            {showOrderStatus ? (
              <li>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-[var(--color-surface-foreground)] hover:bg-black/5"
                  onClick={() => {
                    onOpenOrderStatus?.()
                    onClose()
                  }}
                >
                  <Truck className="h-5 w-5 shrink-0 opacity-70" aria-hidden />
                  Track order
                </button>
              </li>
            ) : null}
            {menuItems.map((item) => {
              const Icon = itemIcon(item.actionType)
              return (
                <li key={item.id || item.label}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-[var(--color-surface-foreground)] hover:bg-black/5"
                    onClick={() => handleItemClick(item)}
                  >
                    <Icon className="h-5 w-5 shrink-0 opacity-70" aria-hidden />
                    {item.label}
                  </button>
                </li>
              )
            })}
          </ul>
        </nav>

        {showCart || showCheckout ? (
          <div className="mt-auto border-t border-[var(--color-border)] px-2 py-3">
            <ul className="flex flex-col gap-0.5">
              {showCart ? (
                <li>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-black/5"
                    onClick={() => {
                      openMenuUrl(
                        ecommerce.viewCartUrl,
                        ochatbot,
                        ecommerce.viewCartLabel || 'View Cart',
                        '_top',
                      )
                      onClose()
                    }}
                  >
                    <ShoppingCart className="h-5 w-5 shrink-0 opacity-70" aria-hidden />
                    {ecommerce.viewCartLabel || 'View Cart'}
                  </button>
                </li>
              ) : null}
              {showCheckout ? (
                <li>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm hover:bg-black/5"
                    onClick={() => {
                      openMenuUrl(
                        ecommerce.checkoutUrl,
                        ochatbot,
                        ecommerce.checkoutLabel || 'Checkout',
                        '_top',
                      )
                      onClose()
                    }}
                  >
                    <Banknote className="h-5 w-5 shrink-0 opacity-70" aria-hidden />
                    {ecommerce.checkoutLabel || 'Checkout'}
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </aside>
    </>
  )
}
